/**
 * Scene 5: review, execution, and release.
 *
 * Renders the manifest, the chevron-flow summary, the PD toggle, the start
 * button, and live execution. The release section is admin-only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, buckets, activeWerke, vorwaertsStufen, pdPidsFuerStufe } from "../context/TransportV2Context";
import { stageLabel } from "../../../utils/stages";
import { useCicdRun } from "../hooks/useCicdRun";
import { useReleaseCandidate } from "../hooks/useReleaseCandidate";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { api } from "../../../utils/helpers";
import { buildConfigsByWerk } from "../../../utils/transport-config-helpers";
import { Cx } from "../components/IconSprite";
import RunLiveCard from "../components/RunLiveCard";

/**
 * How long ago was the last push? The candidate provides a timestamp; the
 * row used to show a blanket "today" regardless of the actual date.
 */
function vorWieLange(iso) {
  if (!iso) return "";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} min`;
  const std = Math.floor(min / 60);
  if (std < 24) return `vor ${std} h`;
  const tage = Math.floor(std / 24);
  return tage === 1 ? "vor 1 Tag" : `vor ${tage} Tagen`;
}

function currentUserRole() {
  try {
    const raw = localStorage.getItem("cpi_user");
    if (raw) return JSON.parse(raw)?.role || null;
  } catch {}
  return null;
}

function selectedIflows(state) {
  const cmp = state.precheckByBucket || {};
  const seen = new Set();
  const list = [];
  for (const entry of Object.values(cmp)) {
    if (!entry || !Array.isArray(entry.comparison)) continue;
    for (const a of entry.comparison) {
      if (a.draft) continue;
      const src = a.originalId || a.Id;
      if (seen.has(src)) continue;
      // has at least one Werk in sel?
      const sel = state.sel?.[src];
      if (!sel) continue;
      const anyOn = Object.keys(sel).some((k) => sel[k]);
      if (!anyOn) continue;
      seen.add(src);
      list.push({ id: src, name: a.Name || src, version: a.Version || "-" });
    }
  }
  return list;
}

function collectSelectedArtifactIds(state) {
  return selectedIflows(state).map((f) => f.id);
}

// The selected plants for the snapshot: it creates one package folder per suffix in Git.
// Without plants the list stays empty and only the source package is written.
function collectWerke(state) {
  if (!hasWerke(state)) return [];
  return activeWerke(state)
    .filter((w) => w.kennung)
    .map((w) => ({ kennung: w.kennung, name: w.name || w.kennung }));
}

export default function Scene5Review({ user }) {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const cicd = useCicdRun();
  const role = user?.role || currentUserRole();
  const isAdmin = role === "admin";
  const rel = useReleaseCandidate(state.spaceId, isAdmin);
  const [pdCounts, setPdCounts] = useState({ pids: 0, chained: 0 });
  const [phases, setPhases] = useState([]);
  const [runReason, setRunReason] = useState("");
  const startedRef = useRef(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const pipe = usePipelineReady(state.spaceId);
  // SAP mode: transport runs through SAP's own workflows. No Partner Directory there.
  const sapModus = pipe.modus === "sap";

  const iflows = useMemo(() => selectedIflows(state), [state]);
  const bucketList = buckets(state);

  // Auto-detected Pids (from BPMN plus ProcessDirect reverse match)
  const [pdAuto, setPdAuto] = useState({ loading: false, primary: [], related: [], error: null });
  // Full candidate list from the source tenant (only Pids with metadata)
  const [pdSource, setPdSource] = useState({ loading: false, entries: [], error: null });
  const [pdSearch, setPdSearch] = useState("");
  const iflowsKey = useMemo(() => iflows.map((f) => f.id).sort().join("|"), [iflows]);
  // Marker for the pre-selection: exactly ONCE per package+iFlow signature.
  // Otherwise a deliberate deselection would be reset on every re-render.
  const prefillKeyRef = useRef(null);

  // Load detected Pids and their count from a single response.
  // Response shape (see backend/lib/transport-api.js /api/transport/partner-references):
  //   partners:        primary + related (the resolved chain)
  //   primaryPartners: directly referenced Pids, either as a string (classic)
  //                    or as an object { pid, via: "reference"|"header", sender, iface, msgType }
  //                    (backend extension: header detection via SAP_Sender/SAP_SenderInterface).
  //   relatedPartners: chained Pids (from determination resolution)
  //   partnerRoles:    { pid: "primary"|"related" }
  useEffect(() => {
    if (sapModus) return;
    if (!state.sourceInstanceId || !state.pkgId) return;
    if (!iflows.length) {
      setPdAuto({ loading: false, primary: [], related: [], error: null });
      setPdCounts({ pids: 0, chained: 0 });
      return;
    }
    let cancelled = false;
    setPdAuto((s) => ({ ...s, loading: true, error: null }));
    api("/api/transport/partner-references", {
      method: "POST",
      body: JSON.stringify({
        sourceInstanceId: state.sourceInstanceId,
        targetInstanceId: state.space?.qasInstance?.id,
        sourcePackageId: state.pkgId,
        artifactIds: iflows.map((f) => f.id),
      }),
    })
      .then((r) => {
        if (cancelled) return;
        // Read both shapes tolerantly: object entries keep via/sender/iface,
        // strings are normalized to {pid, via:"reference"}. On duplicates
        // (same Pid, once via reference, once via header) the first one wins.
        const norm = (arr) => {
          const out = []; const gesehen = new Set();
          for (const raw of Array.isArray(arr) ? arr : []) {
            let pid, via = "reference", sender = null, iface = null, msgType = null;
            if (typeof raw === "string") { pid = raw; }
            else if (raw && typeof raw === "object") {
              pid = raw.pid || raw.Pid || raw.id;
              via = raw.via || "reference";
              sender = raw.sender || null;
              iface = raw.iface || raw.interface || null;
              msgType = raw.msgType || raw.messageType || null;
            }
            if (!pid || gesehen.has(pid)) continue;
            gesehen.add(pid);
            out.push({ pid, via, sender, iface, msgType });
          }
          return out;
        };
        // The backend delivers via/header as top-level maps (keyed by pid),
        // not on the entry itself:
        //   partnerVia:     { [pid]: ["header"|"address"|"reference"] }
        //   partnerHeaders: { [pid]: { sender, iface, msgType } }
        // Pull that into the per-entry shape the UI expects (the header
        // context hint). If entries already are objects with via/sender (a
        // future backend shape), that is preserved as-is.
        const viaMap = r?.partnerVia || {};
        const hdrMap = r?.partnerHeaders || {};
        const anreichern = (p) => {
          const w = viaMap[p.pid];
          const h = hdrMap[p.pid];
          const via = Array.isArray(w) ? (w.includes("header") ? "header" : (w[0] || p.via)) : p.via;
          return { ...p, via, sender: h?.sender ?? p.sender, iface: h?.iface ?? p.iface, msgType: h?.msgType ?? p.msgType };
        };
        const primary = norm(r?.primaryPartners).map(anreichern);
        const related = norm(r?.relatedPartners).map(anreichern);
        setPdAuto({ loading: false, primary, related, error: null });
        setPdCounts({ pids: primary.length, chained: related.length });
        // Pre-selection: exactly ONCE per (package + iFlow signature). A
        // deliberate deselection must be preserved, the marker prevents
        // overwriting it again since the effect reruns on every response.
        const detectedPids = [...new Set([...primary, ...related].map((p) => p.pid))];
        const key = `${state.pkgId}::${iflowsKey}`;
        if (prefillKeyRef.current !== key) {
          prefillKeyRef.current = key;
          dispatch({ type: "SET_PD_SELECTED_PIDS", payload: detectedPids });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPdAuto({ loading: false, primary: [], related: [], error: e?.message || "Ermittlung fehlgeschlagen" });
        setPdCounts({ pids: 0, chained: 0 });
      });
    return () => { cancelled = true; };
    // Deliberately not depending on state.pdSelectedPids here, otherwise
    // every deselection would re-trigger the effect and confuse the
    // pre-selection logic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sourceInstanceId, state.pkgId, state.space, iflowsKey, dispatch, sapModus]);

  // Load the full PD list from the source tenant (for manual additions).
  // Response shape: { partners: [{ Pid, stringParams:[{Id,Value}], binaryParams:[{Id,ContentType}], altPartners:[...], systemGrund }] }
  // systemGrund != null means a system-owned identifier, not selectable.
  useEffect(() => {
    if (sapModus) return; // SAP's route carries no Partner Directory
    if (!state.sourceInstanceId) return;
    if (!state.pdReplicate) return; // only when PD replication is enabled at all
    let cancelled = false;
    setPdSource((s) => ({ ...s, loading: true, error: null }));
    api(`/api/${state.sourceInstanceId}/partner-directory`)
      .then((r) => {
        if (cancelled) return;
        const entries = Array.isArray(r?.partners) ? r.partners : [];
        setPdSource({ loading: false, entries, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setPdSource({ loading: false, entries: [], error: e?.message || "Laden fehlgeschlagen" });
      });
    return () => { cancelled = true; };
  }, [state.sourceInstanceId, state.pdReplicate, sapModus]);

  const startTransport = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    // Off in SAP mode: the snapshot would otherwise put files into Git that nobody reads.
    const usePd = !sapModus && !!state.pdReplicate;
    // Only counts as effective when the checkbox is on AND at least one
    // identifier is selected. Without a selection, the PD phase would be an
    // empty display.
    const selectedPidsNow = usePd ? Array.from(new Set(state.pdSelectedPids || [])) : [];
    const effectivelyPd = usePd && selectedPidsNow.length > 0;
    // No separate PD phase anymore: the Partner Directory travels along in
    // the snapshot (git) and is picked up by the target workflow (wf). There
    // is no separate direct-write step.
    const phaseList = [
      { id: "git", title: t("ph.git"), icon: "ic-git", logs: [] },
      { id: "wf", title: t("ph.wf"), icon: "ic-play", logs: [] },
    ];
    setPhases(phaseList.map((p) => ({ ...p, status: "waiting" })));

    const setPhase = (id, patch) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const appendLog = (id, line) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, logs: [...p.logs, line] } : p)));

    // Phase git
    setPhase("git", { status: "running" });
    for (const f of iflows) appendLog("git", t("log.zip", { name: f.name }));
    appendLog("git", t("log.extract", { pkg: state.pkgName || state.pkgId }));
    if (hasWerke(state)) {
      for (const f of iflows) appendLog("git", t("log.cfg", { id: f.id, n: activeWerke(state).length }));
    }
    // Partner Directory travels along in the snapshot (no more direct write);
    // the target workflow applies the selected entries via a merge-upsert.
    if (effectivelyPd) appendLog("git", t("log.pdSnap", { n: selectedPidsNow.length }));

    setPhase("wf", { status: "waiting" });

    try {
      // Parameter values per plant and stage. The backend turns them into SAP's parameter file
      // per target package and merges them with the Git state - stages that do not appear here
      // stay untouched in Git.
      // state.gitCfg must come along: only by comparing with the Git state can "the user
      // removed the override" be told from "this stage was never maintained". Without it a
      // deleted override would stay in Git forever.
      const configsByWerk = buildConfigsByWerk(state, vorwaertsStufen(state), state.gitCfg);
      const werke = collectWerke(state);
      // Overlay format per forward stage. For a single-stage space this is
      // Ends up as the git commit message in the customer repo, so English
      // and without a dash insertion.
      const commitMessage = `Transport ${state.pkgName || state.pkgId} (${iflows.length} artifacts)`;

      // Final selected Pids (detected-and-selected plus manually added,
      // deduplicated). When pdReplicate is false, the list stays empty, so
      // no PD files get exported to the snapshot.
      const selectedPids = selectedPidsNow;
      // Target stage of the current dispatch (normalized like the backend's
      // normStage: lowercase, no hyphen). Used both to filter the dispatch
      // Pids and for the snapshot contract (pdStagesByPid).
      const targetStageKey = String(state.space?.zielStufe || state.space?.qasInstance?.stage || "qas")
        .toLowerCase()
        .replaceAll("-", "");
      // Only Pids whose stage filter includes the CURRENT target stage (or
      // implicitly "all stages") are sent as partnerIds in the dispatch. Pids
      // marked only for later stages (e.g. PROD only) still live in the
      // snapshot, including .stages.json, but the target workflow skips them
      // here; they take effect only at the later PROD release.
      const dispatchPids = usePd
        ? pdPidsFuerStufe({ pdSelectedPids: selectedPids, pdStagesByPid: state.pdStagesByPid }, targetStageKey)
        : [];
      // pdStagesByPid always goes into the snapshot (the backend already
      // expects it). When a Pid has no filter, the export writes "*"
      // (matching the old behavior).
      const pdStagesByPidPayload = usePd ? (state.pdStagesByPid || {}) : {};

      const res = await cicd.start({
        spaceId: state.spaceId,
        sourceInstanceId: state.sourceInstanceId,
        packageId: state.pkgId,
        packageName: state.pkgName || state.pkgId,
        artifactIds: iflows.map((f) => f.id),
        werke,
        configsByWerk,
        // The snapshot gets ALL selected Pids (including those only for
        // later stages), so their PD files, including .stages.json, are in
        // git, ready for the PROD release.
        snapshotPartnerIds: selectedPids,
        // The dispatch gets only the Pids that should land NOW (the target
        // stage of this dispatch).
        partnerIds: dispatchPids,
        pdStagesByPid: pdStagesByPidPayload,
        commitMessage,
        // Target stage comes from the space's own stage lineup, not a fixed
        // "qas": for a DEV + PRE-PROD + PROD landscape the target is
        // PRE-PROD. The old fallback would have sent the dispatch to QAS,
        // where no such environment exists.
        targetStage: targetStageKey,
      });

      appendLog("git", t("log.commit"));
      setPhase("git", { status: "done" });

      setPhase("wf", { status: "running" });
      appendLog("wf", t("log.wf", {
        // The name has to follow the mode, or the log claims a workflow that never ran.
        wf: sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml",
        s: String(state.space?.zielStufe || state.space?.qasInstance?.stage || "qas").toLowerCase(),
      }));
      if (res.pendingApproval) {
        appendLog("wf", t("log.pending"));
        setPhase("wf", { status: "done" });
        setPendingApproval(true);
      } else if (res.runId) {
        appendLog("wf", t("log.queued", { n: res.runId }));
        setPhase("wf", { status: "done" });
      } else {
        setPhase("wf", { status: "done" });
      }
    } catch (e) {
      const failedId = phases.find((p) => p.status === "running")?.id || "wf";
      setPhase(failedId, { status: "error", error: e?.message || "Fehler" });
      startedRef.current = false;
    }
  }, [state, iflows, cicd, t, phases, sapModus]);

  const [releaseReason, setReleaseReason] = useState("");
  const [relError, setRelError] = useState(null);
  const relReasonOk = releaseReason.trim().length >= 3;
  // /api/cicd/release-candidate responds with {configured, tokenSet, project,
  // candidate}: the candidate sits one level deeper. Reading rel.data.summary
  // or rel.data.items directly is always undefined there, which would make
  // the tiles show the numbers of the running wizard instead of the ones
  // accumulated since the last release, and the list would show this
  // transport's iFlows instead.
  const relCand = rel.data?.candidate || null;
  // The release payload comes FROM the candidate. Without it,
  // POST /api/cicd/transport/release requires packageId and artifactIds and
  // rejects every click with a 400.
  // One candidate per package. Since the plant rework every plant is its own package with its
  // own state - exactly one of them gets released. The UI used to block as soon as several
  // packages had accumulated, because a release can only dispatch one and nobody could tell
  // which was meant. Instead of blocking it now asks.
  const relKandidaten = useMemo(() => (Array.isArray(relCand?.kandidaten) ? relCand.kandidaten : []), [relCand]);
  const [relPkgId, setRelPkgId] = useState(null);
  const relAktiv = relKandidaten.find((k) => k.packageId === relPkgId) || relKandidaten[0] || null;
  const relPayload = relAktiv?.releasePayload || null;
  // After the transport to QAS, a new run appears in the log. Without a
  // reload, the release section would still show the state from BEFORE the
  // transport and have no payload for the release.
  const relReload = rel.reload;
  const relNachgeladen = useRef(false);
  useEffect(() => {
    if (cicd.state === "done" && !relNachgeladen.current) {
      relNachgeladen.current = true;
      relReload();
    }
  }, [cicd.state, relReload]);

  const releaseNow = useCallback(async () => {
    setRelError(null);
    if (!relPayload) { setRelError(t("rel.noPkg")); return; }
    try {
      await rel.releaseNow({ approvalNote: releaseReason.trim(), extra: relPayload });
      setReleaseReason("");
    } catch (e) {
      setRelError(e?.message || "Release fehlgeschlagen");
    }
  }, [rel, releaseReason, relPayload, t]);

  const canStart = iflows.length > 0 && !!state.sourceInstanceId && !!state.pkgId;
  const running = cicd.state === "snapshotting" || cicd.state === "dispatching" || cicd.state === "running";
  const startBtnDisabled = !canStart || running || cicd.state === "done" || pipe.loading || !pipe.ready;

  const s = state.space;
  const stages = s?.stages || [];
  const tgtIdx = Math.max(0, stages.length - 2);

  const runJobs = useMemo(() => {
    if (cicd.runInfo?.jobs?.length) {
      const flat = [];
      for (const j of cicd.runInfo.jobs) {
        for (const st of j.steps || []) {
          flat.push({ name: st.name, status: st.status, duration: st.completed_at && st.started_at ? Math.max(1, Math.round((new Date(st.completed_at) - new Date(st.started_at)) / 1000)) + "s" : "" });
        }
      }
      return flat.slice(0, 12);
    }
    // Fallback: synthesize a plausible step list before live run data is available
    const steps = [t("job.checkout")];
    for (let i = 1; i < stages.length - 1; i++) steps.push(t("job.prep", { code: stages[i].code }));
    for (const f of iflows) steps.push(t("job.upload", { name: f.name }));
    steps.push(t("job.deploy"));
    steps.push(t("job.smoke", { code: stages[tgtIdx]?.code || "QAS" }));
    return steps.map((name) => ({ name, status: "waiting" }));
  }, [cicd.runInfo, stages, iflows, tgtIdx, t]);

  const runNumber = cicd.runInfo?.runNumber || cicd.runId || null;
  const runStatus = cicd.state === "done" ? "success" : cicd.state === "failed" ? "failed" : "running";
  const runMeta = cicd.runInfo?.actor ? `${cicd.runInfo.actor} · ${cicd.runInfo.event || "workflow_dispatch"}` : "";
  const done = cicd.state === "done";

  useEffect(() => {
    if (done) dispatch({ type: "SET_PIPELINE_STATE", payload: { pipeline: "done" } });
  }, [done, dispatch]);

  return (
    <section className="scene active scene-enter" id="s5">
      <div className="sh">
        <h2>{t("s5.h2")}</h2>
        <p>{t("s5.p")}</p>
      </div>

      <div className="manifest">
        <div className="mf-cell">
          <span className="mf-k">{t("mf.space")}</span>
          <span className="mf-v">{s?.name || "-"}</span>
          <span className="mf-s">
            {stages.map((st, i) => (
              <React.Fragment key={"m" + st.code + i}>
                {i > 0 && <Cx />}<span>{st.code}</span>
              </React.Fragment>
            ))}
          </span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.pkg")}</span>
          <span className="mf-v">{state.pkgName || "-"}</span>
          <span className="mf-s">{state.pkgId || ""}</span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.iflows")}</span>
          <span className="mf-v">{t("mf.sel", { n: iflows.length })}</span>
          <span className="mf-s">
            {hasWerke(state)
              ? t("mf.rowsW", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0), m: activeWerke(state).length })
              : t("mf.rows", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0) })}
          </span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.refs")}</span>
          <span className="mf-v">{Object.values(state.refSel || {}).filter(Boolean).length}</span>
          <span className="mf-s">{Object.entries(state.refSel || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "-"}</span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.cfg")}</span>
          <span className="mf-v">{t("mf.req", { n: 0 })}</span>
          <span className="mf-s">{t("mf.allset")}</span>
        </div>
      </div>

      <div className="chevflow">
        <span>{t("cf.iflows", { n: iflows.length })}</span>
        <Cx />
        <span>{state.pkgId || ""}</span>
        {hasWerke(state) && <><Cx /><span>{t("rt.werke")} <span className="ken">{activeWerke(state).map((w) => w.kennung).join(" ")}</span></span></>}
        <Cx />
        <span>{stages[tgtIdx]?.code || "QAS"} · {stages[tgtIdx]?.inst || ""}</span>
        <Cx />
        <span>PROD (PD) · {stages[stages.length - 1]?.inst || ""}</span>
      </div>

      {!pipe.loading && !pipe.ready && (
        <div className="pipe-block">
          <svg className="ic"><use href="#ic-info" /></svg>
          <div className="pb-tx">
            <b>{t("s5.noPipeTitle")}</b>
            <div className="pb-sub">{pipe.configured && !pipe.tokenSet ? t("s5.noPipeToken") : t("s5.noPipeCfg")}</div>
          </div>
          <a className="btn btn-sec" href="/cicd-setup">{t("s5.noPipeLink")}</a>
        </div>
      )}

      {/* SAP mode: SAP's transport carries no Partner Directory. The switch is hidden rather than
          disabled - a control without effect is worse than none. */}
      {(pipe.loading || pipe.ready) && sapModus && (
        <div className="pd-row" style={{ borderLeft: "2px solid var(--warn)", paddingLeft: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label>{t("s5.sapModus")}</label>
            <div className="pd-sub">{t("s5.sapModusSub")}</div>
          </div>
        </div>
      )}
      {(pipe.loading || pipe.ready) && !sapModus && (<>
      <div className="pd-row">
        <input type="checkbox" className="cb" checked={!!state.pdReplicate} onChange={(e) => dispatch({ type: "SET_PD_REPLICATE", payload: e.target.checked })} style={{ marginTop: 3 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <label>{t("s5.pd")}</label>
          <span className="pd-info" tabIndex={0} aria-label={t("s5.pdInfoTitle")}>
            <svg className="ic"><use href="#ic-info" /></svg>
            <div className="pd-info-pop" role="tooltip">
              <div className="pi-title">{t("s5.pdInfoTitle")}</div>
              <ul>
                <li>{t("s5.pdInfoDirect")}</li>
                <li>{t("s5.pdInfoAddr")}</li>
                <li>{t("s5.pdInfoChain")}</li>
              </ul>
              <div className="pi-miss">{t("s5.pdInfoMiss")}</div>
            </div>
          </span>
          <div className="pd-sub">{t("s5.pdSub", { n: pdCounts.pids || 0, m: pdCounts.chained || 0 })}</div>
        </div>
      </div>

      {state.pdReplicate && (
        <PdPicker
          t={t}
          pdAuto={pdAuto}
          pdSource={pdSource}
          pdSearch={pdSearch}
          setPdSearch={setPdSearch}
          selectedPids={state.pdSelectedPids || []}
          pdStagesByPid={state.pdStagesByPid || {}}
          // With only a single forward stage, a toggle would be pointless;
          // PdPicker hides it entirely in that case and the view looks like
          // it always did.
          forwardStages={vorwaertsStufen(state)}
          onToggle={(pid) => dispatch({ type: "TOGGLE_PD_PID", payload: pid })}
          onSetAll={(pids) => dispatch({ type: "SET_PD_SELECTED_PIDS", payload: pids })}
          onToggleStage={(pid, stage) => dispatch({ type: "TOGGLE_PD_STAGE", payload: { pid, stage } })}
        />
      )}

      </>)}

      {/* Outside both mode blocks: in SAP mode there would be nothing left to press. */}
      {(pipe.loading || pipe.ready) && (
        <div className="cta-row">
          <button className="btn btn-cta" onClick={startTransport} disabled={startBtnDisabled}>
            {running ? t("s5.running") : (<><svg className="ic"><use href="#ic-play" /></svg>{t("s5.start")}</>)}
          </button>
          <span className="foot-hint">Branch development · {sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml"}</span>
        </div>
      )}

      {pendingApproval && (
        <div className="sect panel panel-pad">
          <div className="st st--warn"><i className="dot" />{t("s5.pendingApproval")}</div>
        </div>
      )}

      {phases.length > 0 && (
        <div className="sect">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t("s5.execTitle")}</h3>
            <span className="tiny muted">{state.pdReplicate && (state.pdSelectedPids || []).length > 0 ? t("s5.metaWith", { user: user?.displayName || user?.username || "system" }) : t("s5.metaWithout", { user: user?.displayName || user?.username || "system" })}</span>
          </div>
          <div>
            {phases.map((p) => (
              <div key={p.id} className={"phase-card " + (p.status === "running" ? "run" : p.status === "done" ? "done" : p.status === "error" ? "run" : "")}>
                <div className="pc-h">
                  <span className="ph-ind">
                    {p.status === "running" && <span className="spinner" />}
                    {p.status === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <svg className="ic" style={{ color: "var(--t3)" }}><use href={"#" + p.icon} /></svg>
                  <span className="pc-title">{p.title}</span>
                  <span className="tiny muted">{p.status === "done" ? t("s5.done") : ""}</span>
                </div>
                <div className="pc-log">
                  {p.logs.map((line, i) => (
                    <div key={i} className="log-line in"><span className="ok-mark">✓</span>{line}</div>
                  ))}
                  {p.error && <div className="errline">{p.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(cicd.state === "running" || cicd.state === "done" || cicd.state === "failed") && !pendingApproval && (
        <div className="sect">
          <RunLiveCard
            stages={stages}
            tgtIdx={tgtIdx}
            runInfo={cicd.runInfo}
            jobs={runJobs}
            runNumber={runNumber}
            runStatus={runStatus}
            runMeta={runMeta}
            done={done}
            doneText={t("s5.qasOk")}
            doneSub={t("s5.qasOkSub")}
          />
          {/* Partial failure: with several plants each target package gets its own run. If one
              of them does not start, the rest continues - the user must not miss that, or the
              transport looks complete. The bar carries the colour, the text stays readable
              (semantic colour does not carry as text colour). */}
          {cicd.error && (
            <div
              role="status"
              style={{
                marginTop: 10,
                padding: "8px 12px",
                borderLeft: "2px solid var(--warn)",
                background: "color-mix(in srgb, var(--warn) 8%, transparent)",
                fontSize: 13,
                color: "var(--t1)",
              }}
            >
              {cicd.error}
            </div>
          )}
        </div>
      )}

      {isAdmin && pipe.ready && (cicd.state === "done" || relKandidaten.length > 0) && (
        <div className="sect">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t("rel.title")}</h3>
            <span className="tiny muted">{t("rel.meta")}</span>
          </div>
          <div className="rel-tiles">
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.iflows ?? iflows.length}</div><div className="rt-k">{t("rt.iflows")}</div></div>
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.pushes ?? 1}</div><div className="rt-k">{t("rt.pushes", { cx: <Cx /> })}</div></div>
            <div className="rel-tile"><div className="rt-v">{relCand?.summary?.pakete ?? 1}</div><div className="rt-k">{t("rt.pakete")}</div></div>
            <div className="rel-tile"><div className="rt-v">{relAktiv?.summary?.iflows ?? iflows.length}</div><div className="rt-k">{t("rt.prod")}</div></div>
          </div>
          {/* Only ask when there is something to decide: with one package the view stays as
              lean as before. */}
          {relKandidaten.length > 1 && (
            <div className="rel-list" role="radiogroup" aria-label={t("rel.pickPkg")} style={{ marginBottom: 12 }}>
              <div className="rel-row" style={{ background: "color-mix(in srgb, var(--bg2) 55%, transparent)" }}>
                <b className="tiny" style={{ letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)" }}>{t("rel.pickPkg")}</b>
              </div>
              {relKandidaten.map((k) => {
                const gewaehlt = k.packageId === (relAktiv?.packageId || "");
                return (
                  <label key={k.packageId} className="rel-row" style={{ cursor: "pointer", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input
                        type="radio"
                        name="rel-paket"
                        checked={gewaehlt}
                        onChange={() => setRelPkgId(k.packageId)}
                        style={{ accentColor: "var(--signal)" }}
                      />
                      <b>{k.packageName || k.packageId}</b>
                      <span className="art-id">· {k.packageId}</span>
                    </span>
                    <span className="rr-meta">{t("rel.pkgMeta", { n: k.summary.iflows, wann: vorWieLange(k.lastAt) })}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="rel-list">
            <div className="rel-row" style={{ background: "color-mix(in srgb, var(--bg2) 55%, transparent)" }}>
              <b className="tiny" style={{ letterSpacing: ".1em", textTransform: "uppercase", color: "var(--t3)" }}>{t("rel.accum")}</b>
            </div>
            {(relAktiv?.items?.length ? relAktiv.items : iflows.map((f) => ({ iflow: f.name, packageId: state.pkgId }))).map((it, i) => (
              <div key={i} className="rel-row">
                <span><b>{it.iflow || it.name}</b> <span className="art-id">· {it.packageId || state.pkgId}</span></span>
                <span className="rr-meta">
                  {it.pushes
                    ? t("rel.push", { n: it.pushes, cx: <Cx />, wann: vorWieLange(it.lastAt) })
                    : t("rel.today", { cx: <Cx /> })}
                </span>
              </div>
            ))}
          </div>
          <div className="panel rel-form">
            <label className="grp-label" style={{ margin: 0 }}>{t("rel.reason")}</label>
            <textarea
              value={releaseReason}
              placeholder={t("rel.ph")}
              onChange={(e) => setReleaseReason(e.target.value)}
              disabled={rel.releasing}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, alignItems: "center" }}>
              <span className="foot-hint">
                {!relPayload ? t("rel.noPkg") : relReasonOk ? t("rel.ready") : t("rel.min3")}
              </span>
              <button className="btn btn-cta" onClick={releaseNow} disabled={!relReasonOk || !relPayload || rel.releasing}>
                {rel.releasing ? t("rel.running") : t("rel.btn")}
              </button>
            </div>
            {(relError || rel.releaseError) && (
              <div className="errline">{relError || rel.releaseError}</div>
            )}
          </div>
        </div>
      )}

      <div className={"final-banner" + (state.finalized ? " show" : "")}>
        <h3>{t("fin.h")}</h3>
        <p>{t("fin.txt", { name: state.pkgName || state.pkgId })}</p>
        <button className="btn btn-sec" onClick={() => dispatch({ type: "RESET_ALL" })}>{t("fin.restart")}</button>
      </div>

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 4 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <span className="foot-hint">{t("s5.footHint")}</span>
      </div>
    </section>
  );
}

/**
 * PdPicker: two blocks for the Partner Directory selection.
 *   1) Auto-detected (from BPMN plus ProcessDirect reverse match), pre-selected.
 *   2) Add your own (the full list from the source tenant, system-owned
 *      entries grayed out).
 *
 * Deliberately kept as a sub-component in this same file: no new file for a
 * view that only exists inside Scene5Review.
 */
function PdPicker({ t, pdAuto, pdSource, pdSearch, setPdSearch, selectedPids, pdStagesByPid, forwardStages, onToggle, onSetAll, onToggleStage }) {
  const selSet = new Set(selectedPids);
  // Normalized: primary/related are arrays of {pid, via, sender, iface, msgType}.
  const primary = pdAuto.primary || [];
  const related = pdAuto.related || [];
  // Pid set (deduplicated), used to exclude entries from "Add your own".
  const detectedPidSet = new Set([...primary.map((p) => p.pid), ...related.map((p) => p.pid)]);
  // Display order: primary before related; when a Pid appears in both, the
  // primary entry is enough, the related duplicate would be a redundant row.
  const primaryPids = new Set(primary.map((p) => p.pid));
  const anzeige = [
    ...primary.map((p) => ({ ...p, rolle: p.via === "header" ? "header" : "primary" })),
    ...related.filter((p) => !primaryPids.has(p.pid)).map((p) => ({ ...p, rolle: "related" })),
  ];

  // Stage toggle: only makes sense with more than one forward stage. With
  // exactly one forward stage (single-stage space, target stage equals the
  // last stage), the block disappears entirely.
  const stufenListe = Array.isArray(forwardStages) ? forwardStages : [];
  const hatMehrereStufen = stufenListe.length > 1;
  const pidStufen = pdStagesByPid || {};
  const allStageKeys = stufenListe.map((s) => s.key);
  // Renders the stage row for a Pid. The caller decides whether the row is
  // shown at all (only when the Pid is selected AND more than one stage
  // exists).
  const renderStufen = (pid) => {
    if (!hatMehrereStufen) return null;
    if (!selSet.has(pid)) return null;
    // When this Pid has no entry (can happen via external render paths), all
    // stages implicitly apply, matching the backend default "*".
    const aktive = Array.isArray(pidStufen[pid]) ? new Set(pidStufen[pid]) : new Set(allStageKeys);
    return (
      <div className="pd-stages" role="group" aria-label={t("s5.pdStagesAria", { pid })}>
        <span className="pd-stages-lbl">{t("s5.pdStagesLbl")}</span>
        {stufenListe.map((st) => {
          const an = aktive.has(st.key);
          return (
            <button
              key={st.key}
              type="button"
              className={"pd-stage-toggle" + (an ? " is-on" : "")}
              aria-pressed={an}
              // Clicking the row (a label) would otherwise toggle the Pid
              // checkbox; this click belongs to the stage row, not to the Pid
              // selection.
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleStage(pid, st.key); }}
              title={t(an ? "s5.pdStageOn" : "s5.pdStageOff", { code: st.code || stageLabel(st.key) })}
            >
              <span className="pd-stage-mark" aria-hidden>{an ? "✓" : ""}</span>
              <span className="pd-stage-code">{st.code || stageLabel(st.key)}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // "Add your own": everything from pdSource MINUS auto-detected entries (via
  // reference or via header, both already shown in block 1). System-owned
  // identifiers remain as a grayed-out row.
  const custom = (pdSource.entries || []).filter((e) => !detectedPidSet.has(e.Pid));
  const q = pdSearch.trim().toLowerCase();
  const customFiltered = q ? custom.filter((e) => e.Pid.toLowerCase().includes(q)) : custom;

  // Counts for the header row.
  const selectableCustom = custom.filter((e) => !e.systemGrund);
  const selCustomCount = selectableCustom.filter((e) => selSet.has(e.Pid)).length;
  const selAutoCount = [...detectedPidSet].filter((p) => selSet.has(p)).length;
  const totalSelected = selectedPids.length;
  const totalDetected = detectedPidSet.size;

  const setAllCustom = (on) => {
    const cur = new Set(selectedPids);
    for (const e of selectableCustom) {
      if (on) cur.add(e.Pid); else cur.delete(e.Pid);
    }
    onSetAll([...cur]);
  };
  const setAllAuto = (on) => {
    const cur = new Set(selectedPids);
    for (const p of detectedPidSet) {
      if (on) cur.add(p); else cur.delete(p);
    }
    onSetAll([...cur]);
  };

  return (
    <div className="pd-picker">
      {/* Summary line */}
      <div className="pd-picker-sum">
        {totalSelected > 0
          ? t("s5.pdSelSummary", { n: totalSelected })
          : t("s5.pdSelSummaryNone")}
      </div>

      {/* Block 1: auto-detected */}
      <div className="pd-block">
        <div className="pd-block-h">
          <div>
            <div className="pd-block-title">{t("s5.pdAutoTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdAutoSub")}</div>
          </div>
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selAutoCount, m: totalDetected })}</span>
            {totalDetected > 0 && (
              <>
                <button type="button" className="pd-mini-btn" onClick={() => setAllAuto(true)}>{t("s5.pdAll")}</button>
                <button type="button" className="pd-mini-btn pd-mini-btn--ghost" onClick={() => setAllAuto(false)}>{t("s5.pdNone")}</button>
              </>
            )}
          </div>
        </div>
        {pdAuto.loading && <div className="pd-empty">{t("s5.pdAutoLoading")}</div>}
        {!pdAuto.loading && totalDetected === 0 && <div className="pd-empty">{t("s5.pdAutoNone")}</div>}
        {!pdAuto.loading && totalDetected > 0 && (
          <div className="pd-list">
            {anzeige.map((eintrag) => {
              // The role determines the text plus an optional header context (sender/interface).
              const rolleTxt = eintrag.rolle === "related"
                ? t("s5.pdRoleRelated")
                : eintrag.rolle === "header"
                  ? t("s5.pdRoleHeader")
                  : t("s5.pdRolePrimary");
              const headerCtx = eintrag.rolle === "header" && eintrag.sender
                ? (eintrag.iface
                    ? t("s5.pdHeaderCtx", { s: eintrag.sender, i: eintrag.iface })
                    : t("s5.pdHeaderCtxShort", { s: eintrag.sender }))
                : null;
              return (
                <div key={eintrag.pid} className="pd-item-wrap">
                  <label className="pd-item">
                    <input
                      type="checkbox"
                      className="cb"
                      checked={selSet.has(eintrag.pid)}
                      onChange={() => onToggle(eintrag.pid)}
                    />
                    <span className="pd-pid">{eintrag.pid}</span>
                    {headerCtx && <span className="pd-meta">{headerCtx}</span>}
                    <span className="pd-role">{rolleTxt}</span>
                  </label>
                  {renderStufen(eintrag.pid)}
                </div>
              );
            })}
          </div>
        )}
        {pdAuto.error && !pdAuto.loading && (
          <div className="pd-empty" style={{ color: "var(--warn)" }}>{pdAuto.error}</div>
        )}
        {hatMehrereStufen && totalDetected > 0 && !pdAuto.loading && (
          <div className="pd-stages-hint">{t("s5.pdStagesHint")}</div>
        )}
      </div>

      {/* Block 2: add your own */}
      <div className="pd-block">
        <div className="pd-block-h">
          <div>
            <div className="pd-block-title">{t("s5.pdCustomTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdCustomSub")}</div>
          </div>
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selCustomCount, m: selectableCustom.length })}</span>
            {selectableCustom.length > 0 && (
              <>
                <button type="button" className="pd-mini-btn" onClick={() => setAllCustom(true)}>{t("s5.pdAll")}</button>
                <button type="button" className="pd-mini-btn pd-mini-btn--ghost" onClick={() => setAllCustom(false)}>{t("s5.pdNone")}</button>
              </>
            )}
          </div>
        </div>
        {pdSource.loading && <div className="pd-empty">{t("s5.pdCustomLoading")}</div>}
        {!pdSource.loading && (pdSource.entries || []).length === 0 && (
          <div className="pd-empty">{t("s5.pdCustomEmpty")}</div>
        )}
        {!pdSource.loading && custom.length > 0 && (
          <>
            <input
              type="text"
              className="pd-search"
              placeholder={t("s5.pdCustomSearch")}
              value={pdSearch}
              onChange={(e) => setPdSearch(e.target.value)}
            />
            <div className="pd-list pd-list--scroll">
              {customFiltered.map((entry) => {
                const isSystem = !!entry.systemGrund;
                const meta = t("s5.pdCountsMeta", {
                  s: (entry.stringParams || []).length,
                  b: (entry.binaryParams || []).length,
                  a: (entry.altPartners || []).length,
                });
                return (
                  <div key={entry.Pid} className="pd-item-wrap">
                    <label className={"pd-item" + (isSystem ? " pd-item--disabled" : "")}>
                      <input
                        type="checkbox"
                        className="cb"
                        disabled={isSystem}
                        checked={!isSystem && selSet.has(entry.Pid)}
                        onChange={() => !isSystem && onToggle(entry.Pid)}
                      />
                      <span className="pd-pid">{entry.Pid}</span>
                      <span className="pd-meta">{meta}</span>
                      {isSystem && <span className="pd-sys">{t("s5.pdSystem")}</span>}
                    </label>
                    {!isSystem && renderStufen(entry.Pid)}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {pdSource.error && !pdSource.loading && (
          <div className="pd-empty" style={{ color: "var(--warn)" }}>{pdSource.error}</div>
        )}
      </div>
    </div>
  );
}
